import type { User as UserDto } from '@src/dto/User';
import { isRight, left, right } from 'fp-ts/Either';
import Cookies from 'js-cookie';
import { createContext, use, useEffect, useState } from 'react';

import { TRPC_URL } from '../lib/config';
import { unknownToLeft } from '../lib/unknownToLeft';
import { Result } from '../types/Result';
import { captureAndReportError } from '../utils/errorReporting';
import { trackCount, trackDuration } from '../utils/sentryMetrics';
import { TRPCContext } from './TRPC';
export const COOKIE_NAME = 'user';
const AUTH_EVENT_METRIC = 'portal.web.auth.event';
const AUTH_DURATION_METRIC = 'portal.web.auth.duration';

function trackAuthEvent(
  event: string,
  outcome: string,
  durationMs?: number,
): void {
  const attributes = { event, outcome };
  trackCount(AUTH_EVENT_METRIC, 1, attributes);

  if (durationMs !== undefined && Number.isFinite(durationMs)) {
    trackDuration(AUTH_DURATION_METRIC, durationMs, attributes);
  }
}

export type UserOnboardingState = UserDto['onboardingState'];
type AuthenticatedUser = UserDto & { jwt: string };

export enum AuthStateType {
  NoToken = 'NoToken',
  Verifying = 'Verifying',
  Authenticated = 'Authenticated',
}

export interface AuthStateNoToken {
  type: AuthStateType.NoToken;
}

export interface AuthStateVerifying {
  type: AuthStateType.Verifying;
}

export interface AuthStateAuthenticated {
  type: AuthStateType.Authenticated;
  user: AuthenticatedUser;
}

export type AuthState =
  | AuthStateNoToken
  | AuthStateVerifying
  | AuthStateAuthenticated;

interface Auth {
  state: AuthState;
  setOnboardingState(userOnboardingState: UserOnboardingState): void;
  refreshUser: () => Promise<Result<boolean>>;
  signIn: (
    email: string,
    password: string,
  ) => Promise<Result<AuthenticatedUser>>;
  signUp: (
    email: string,
    fullName: string,
    password: string,
    passwordRepeat: string,
  ) => Promise<Result<AuthenticatedUser>>;
  signOut: () => Promise<Result<boolean>>;
}

export const AuthStateContext = createContext<Auth>({
  state: { type: AuthStateType.Verifying },
  setOnboardingState: () =>
    left({ name: 'NotImplementedError', message: 'Method not implemented' }),
  refreshUser: async () =>
    left({ name: 'NotImplementedError', message: 'Method not implemented' }),
  signIn: async () =>
    left({ name: 'NotImplementedError', message: 'Method not implemented' }),
  signUp: async () =>
    left({ name: 'NotImplementedError', message: 'Method not implemented' }),
  signOut: async () =>
    left({ name: 'NotImplementedError', message: 'Method not implemented' }),
});

interface Props {
  children?: React.ReactNode;
}

export function AuthStateProvider(props: Props) {
  const [state, setState] = useState<AuthState>({
    type: AuthStateType.Verifying,
  });
  const trpc = use(TRPCContext);

  useEffect(() => {
    const jwt = Cookies.get(COOKIE_NAME);
    if (jwt) {
      const startedAt = Date.now();
      setState({ type: AuthStateType.Verifying });

      trpc.setup(TRPC_URL, jwt);

      trpc.client.fetchUserByJwt.query({ jwt }).then((result) => {
        if (isRight(result)) {
          const userData = result.right;

          trackAuthEvent('bootstrap_verify', 'success', Date.now() - startedAt);
          setState({
            type: AuthStateType.Authenticated,
            user: { jwt, ...userData },
          });
        } else {
          trackAuthEvent('bootstrap_verify', 'failure', Date.now() - startedAt);
          captureAndReportError(result.left);
          Cookies.remove(COOKIE_NAME);
          setState({ type: AuthStateType.NoToken });
          trpc.setup(TRPC_URL, null);
        }
      });
    } else {
      trackAuthEvent('bootstrap_no_token', 'success');
      setState({ type: AuthStateType.NoToken });
    }
  }, []);

  return (
    <AuthStateContext.Provider
      value={{
        state,
        setOnboardingState: (onboardingState) => {
          setState((cur) => {
            if (cur.type === AuthStateType.Authenticated) {
              const result = { ...cur, user: { ...cur.user, onboardingState } };
              return result;
            }

            return cur;
          });
        },
        refreshUser: async () => {
          const startedAt = Date.now();
          const jwt = Cookies.get(COOKIE_NAME);

          if (!jwt) {
            trackAuthEvent('refresh_user', 'no_token', Date.now() - startedAt);
            return left({
              name: 'NoTokenError',
              message: 'No JWT token found',
            });
          }

          try {
            const result = await trpc.client.fetchUserByJwt.query({ jwt });

            if (isRight(result)) {
              const userData = result.right;
              setState({
                type: AuthStateType.Authenticated,
                user: { jwt, ...userData },
              });
              trackAuthEvent('refresh_user', 'success', Date.now() - startedAt);
              return right(true);
            } else {
              trackAuthEvent('refresh_user', 'failure', Date.now() - startedAt);
              captureAndReportError(result.left);
              return left({
                name: 'FetchError',
                message: 'Failed to fetch user data',
              });
            }
          } catch (e) {
            trackAuthEvent('refresh_user', 'error', Date.now() - startedAt);
            return unknownToLeft(e);
          }
        },
        signIn: async (email: string, password: string) => {
          const startedAt = Date.now();
          const result = await trpc.client.signIn
            .mutate({ email, password })
            .catch(unknownToLeft);

          if (isRight(result)) {
            const user = result.right as AuthStateAuthenticated['user'];
            Cookies.set(COOKIE_NAME, user.jwt);

            setState({
              type: AuthStateType.Authenticated,
              user,
            });

            trpc.setup(TRPC_URL, user.jwt);

            const refreshResult = await trpc.client.fetchUserByJwt.query({
              jwt: user.jwt,
            });
            if (isRight(refreshResult)) {
              const userData = refreshResult.right;
              setState({
                type: AuthStateType.Authenticated,
                user: { jwt: user.jwt, ...userData },
              });
            }
            trackAuthEvent('sign_in', 'success', Date.now() - startedAt);
          } else {
            trackAuthEvent('sign_in', 'failure', Date.now() - startedAt);
            captureAndReportError(result.left);
          }

          return result;
        },
        signUp: async (
          email: string,
          fullName: string,
          password: string,
          passwordRepeat: string,
        ) => {
          const startedAt = Date.now();
          const result = await trpc.client.signUp
            .mutate({
              email,
              fullName,
              password,
              passwordRepeat,
            })
            .catch(unknownToLeft);

          if (isRight(result)) {
            const user = result.right as AuthStateAuthenticated['user'];
            Cookies.set(COOKIE_NAME, user.jwt);

            setState({
              type: AuthStateType.Authenticated,
              user,
            });

            trpc.setup(TRPC_URL, user.jwt);
            trackAuthEvent('sign_up', 'success', Date.now() - startedAt);
          } else {
            trackAuthEvent('sign_up', 'failure', Date.now() - startedAt);
            captureAndReportError(result.left);
          }

          return result;
        },
        signOut: async () => {
          const startedAt = Date.now();
          try {
            Cookies.remove(COOKIE_NAME);
            setState({ type: AuthStateType.NoToken });
            trpc.setup(TRPC_URL, null);
            trackAuthEvent('sign_out', 'success', Date.now() - startedAt);
            return right(true);
          } catch (e) {
            trackAuthEvent('sign_out', 'failure', Date.now() - startedAt);
            return unknownToLeft(e);
          }
        },
      }}
    >
      {props.children}
    </AuthStateContext.Provider>
  );
}
